<script setup lang="ts">
import type { Ref } from "vue"
import type { EventTemplate } from "@/models"
import type { IEventThemeJsonRequestV1 } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { GET_EVENT_THEME, useGetEventTheme, useUpdateEventTheme } from "@/api"
import { EventThemeSelector } from "@/components"
import { Button } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/customize", value => Number(value))

const queryClient = useQueryClient()
const { t } = useTranslation()
const toast = useToast()

const updatedTheme: Ref<Partial<EventTemplate> | undefined> = ref()

const { data: eventTheme, isLoading: isEventThemeLoading, isRefetching: isEventThemeRefetching } = useGetEventTheme(eventVaultId)
const { mutate: updateEventTheme, isPending: isUpdatingEventTheme } = useUpdateEventTheme(eventVaultId)

const isLoading = computed(() => isEventThemeLoading.value || isUpdatingEventTheme.value || isEventThemeRefetching.value)

function onSubmit() {
  const theme: IEventThemeJsonRequestV1 = {
    tokens: updatedTheme.value?.themeJson?.tokens,
    typography: updatedTheme.value?.themeJson?.typography,
  }

  updateEventTheme(theme, {
    onSuccess: () => {
      toast.success({ description: t("customize_page.success_toast") })
      queryClient.invalidateQueries({ queryKey: [GET_EVENT_THEME, eventVaultId] })
    },
    onError: () => {
      toast.error()
    },
  })
}
</script>

<template>
  <SidebarHostEventLayout>
    <div class="flex flex-col gap-8">
      <div class="flex flex-col gap-2 max-w-4xl">
        <h1 class="font-comfortaa font-bold text-xl mb-2">
          {{ t("customize_page.title") }}
        </h1>
        <p class="text-label text-sm md:text-base font-montserrat">
          {{ t("customize_page.message") }}
        </p>
      </div>

      <EventThemeSelector
        v-model:theme="updatedTheme"
        :is-loading="isLoading"
        :custom-template="eventTheme"
        @save="onSubmit"
      />

      <Button class="md:w-fit md:px-20!" @click="onSubmit">
        {{ isUpdatingEventTheme ? t("customize_page.saving") : t("customize_page.save_theme_btn") }}
      </Button>
    </div>
  </SidebarHostEventLayout>
</template>
